import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useWallet } from "../context/walletContext";
import { formatAmount } from "../utils/formatAmount";
import { useNotify } from "../context/NotifyContext";
import { TransferAddMoneyToMainWallet } from "../api/addmoneyApi";

const MainWallet = ({ open, close }) => {
  const { walletAmt } = useWallet();
  const {notify} = useNotify();
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");

  // Compute transferable dynamically
  const getTransferable = () => {
    return Number(walletAmt?.addMoney?.balance || 0) - Number(walletAmt?.addMoney?.hold || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const num = Number(amount);

    if (!amount) return notify("Please enter amount", "warning");
    if (isNaN(num)) return notify("Amount must be a valid number", "error");
    if (num <= 0) return notify("Amount must be greater than 0", "warning");
    if (!/^\d+(\.\d{1,2})?$/.test(amount)) return notify("Only up to 2 decimal places allowed", "warning");
    if (num > getTransferable()) return notify("Amount cannot be greater than transferable balance", "error");
    if (!remarks.trim()) return notify("Remarks are required", "warning");

    const payload = { amount: num, remarks: remarks.trim() };

    try {
      setLoading(true);
      const res = await TransferAddMoneyToMainWallet(payload);
      if (res.success) {
        notify(res.message || "Amount transferred to Main Wallet", "success");
        close();
        setAmount("");
        setRemarks("");
      } else {
        notify(res?.message || "Transfer Failed", "error");
      }
    } catch (err) {
      notify(err.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[9999]">
      <div
        className="ds-modal relative w-[90%] max-h-[85vh] overflow-y-auto ds-scroll md:w-[95%] md:max-w-[750px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center pb-3 border-b border-[var(--border-color)]">
          <h2 className="text-lg font-semibold text-[var(--text-dark)]">
            Move Balance from Add Money Wallet to Main Wallet
          </h2>
          <button type="button" onClick={close}>
            <CloseIcon className="cursor-pointer text-[var(--text-muted)]" />
          </button>
        </div>

        <form className="space-y-4 mt-4" onSubmit={handleSubmit}>
          {/* Wallet Balances */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="ds-label">
                Available Balance In Add Money Wallet <span className="text-[var(--error-color)]">*</span>
              </label>
              <input
                type="text"
                value={formatAmount(walletAmt?.addMoney?.balance)}
                readOnly
                className="ds-input text-sm bg-[var(--bg-app)] cursor-not-allowed"
              />
            </div>
            <div>
              <label className="ds-label">
                Hold Balance In Add Money Wallet <span className="text-[var(--error-color)]">*</span>
              </label>
              <input
                type="text"
                value={formatAmount(walletAmt?.addMoney?.hold)}
                readOnly
                className="ds-input text-sm bg-[var(--bg-app)] cursor-not-allowed"
              />
            </div>
          </div>

          {/* Transferable Balance */}
          <div>
            <label className="ds-label">
              Transferable Balance <span className="text-[var(--error-color)]">*</span>
            </label>
            <input
              type="text"
              value={formatAmount(getTransferable())}
              readOnly
              className="ds-input text-sm bg-[var(--bg-app)] cursor-not-allowed"
            />
          </div>

          {/* Amount & Charges */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="ds-label">
                Enter Amount <span className="text-[var(--error-color)]">*</span>
              </label>
              <input
                type="text"
                value={amount}
                placeholder="Enter Amount"
                className="ds-input text-sm"
                onChange={(e) => {
                  let val = e.target.value.trim();
                  if (!/^\d*\.?\d{0,2}$/.test(val)) return; // allow only 2 decimals
                  if (Number(val) > getTransferable()) return; // max transferable
                  setAmount(val);
                }}
              />
            </div>
            <div>
              <label className="ds-label">Charges (If Applicable)</label>
              <input
                type="text"
                value="0.00 %"
                readOnly
                className="ds-input text-sm bg-[var(--bg-app)] cursor-not-allowed"
              />
            </div>
          </div>

          {/* Remarks */}
          <div>
            <label className="ds-label">
              Remarks <span className="text-[var(--error-color)]">*</span>
            </label>
            <textarea
              placeholder="Enter Your Remarks..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="ds-input text-sm h-24 resize-none"
            />
          </div>

          <div className="ds-form-actions">
            <button
              type="submit"
              disabled={!amount || !remarks.trim() || loading}
              className="ds-btn ds-btn-primary"
            >
              {loading ? "Transferring..." : "Transfer To Main Wallet"}
            </button>
            <button
              type="button"
              onClick={close}
              className="ds-btn ds-btn-secondary"
            >
              Cancel Transfer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MainWallet;
